import { createContext, useContext, useState, useCallback, useEffect } from 'react';

/**
 * Junto Frontend Cart Context
 * 
 * This context provides cart functionality that can be used across any studio
 * with full theme customization support. It handles cart state management,
 * persistence, and promo code integration.
 */

const CartContext = createContext();

// Cookie utility functions
const setCookie = (name, value, days = 7) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${JSON.stringify(value)};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for(let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      try {
        return JSON.parse(c.substring(nameEQ.length, c.length));
      } catch (e) {
        return null;
      }
    }
  }
  return null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    // Load cart from cookies on initialization
    const savedCart = getCookie('sol_room_cart');
    return savedCart && Array.isArray(savedCart) ? savedCart : [];
  });
  
  const [promoCode, setPromoCode] = useState(() => {
    // Load promo code from cookies on initialization
    return getCookie('sol_room_promo_code') || null;
  });
  
  const [promoDiscount, setPromoDiscount] = useState(() => {
    // Load promo discount from cookies on initialization
    return getCookie('sol_room_promo_discount') || null;
  });

  // Save cart to cookies whenever it changes
  useEffect(() => {
    setCookie('sol_room_cart', cartItems, 30); // Save for 30 days
  }, [cartItems]);

  const addToCart = useCallback((item) => {
    setCartItems(prev => {
      // Check if item already exists (same id and recurring status)
      const existingIndex = prev.findIndex(
        cartItem => cartItem.id === item.id && cartItem.isRecurring === item.isRecurring
      );
      
      if (existingIndex >= 0) {
        // Update existing item quantity
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + item.quantity
        };
        return updated;
      } else {
        // Add new item
        return [...prev, { ...item, cartId: Date.now() + Math.random() }];
      }
    });
  }, []);

  const updateCartItem = useCallback((cartId, quantity) => {
    setCartItems(prev => 
      prev.map(item => 
        item.cartId === cartId 
          ? { ...item, quantity: Math.max(0, quantity) }
          : item
      )
    );
  }, []);

  const removeFromCart = useCallback((cartId) => {
    setCartItems(prev => prev.filter(item => item.cartId !== cartId));
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const getCartTotal = useCallback(() => {
    return cartItems.reduce((total, item) => {
      // Only apply 5% discount for recurring non-membership items
      const itemPrice = (item.isRecurring && item.itemType !== 'membership') ? item.price * 0.95 : item.price;
      return total + (itemPrice * item.quantity);
    }, 0);
  }, [cartItems]);

  const getCartItemCount = useCallback(() => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  }, [cartItems]);

  // Save promo code to cookies whenever it changes
  useEffect(() => {
    if (promoCode) {
      setCookie('sol_room_promo_code', promoCode, 30);
    } else {
      deleteCookie('sol_room_promo_code');
    }
  }, [promoCode]);

  // Save promo discount to cookies whenever it changes
  useEffect(() => {
    if (promoDiscount) {
      setCookie('sol_room_promo_discount', promoDiscount, 30);
    } else {
      deleteCookie('sol_room_promo_discount');
    }
  }, [promoDiscount]);

  const applyPromoCode = useCallback((code, discountData) => {
    setPromoCode(code);
    setPromoDiscount(discountData);
  }, []);

  const removePromoCode = useCallback(() => {
    setPromoCode(null);
    setPromoDiscount(null);
  }, []);

  const getCartTotalWithPromo = useCallback(() => {
    const baseTotal = getCartTotal();
    if (!promoDiscount) return baseTotal;
    
    const discountAmount = promoDiscount.total_discount || 0;
    return Math.max(0, baseTotal - discountAmount);
  }, [getCartTotal, promoDiscount]);

  const getItemDiscount = useCallback((item) => {
    if (!promoDiscount || !promoDiscount.applied_items) return null;
    
    const appliedItem = promoDiscount.applied_items.find(
      appliedItem => appliedItem.id === item.id || appliedItem.name === item.name
    );
    
    return appliedItem ? appliedItem.discount : null;
  }, [promoDiscount]);

  const value = {
    cartItems,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    getCartTotal,
    getCartItemCount,
    promoCode,
    promoDiscount,
    applyPromoCode,
    removePromoCode,
    getCartTotalWithPromo,
    getItemDiscount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};